import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import { formatApiDetail } from "@/utils/showApiError";
import "@/index.css";
import "@/i18n";
import App from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

// --- FORK-AWARE REDIRECT ----------------------------------------------------
// When we ship a new fork, the preview URL changes (e.g.
// `drip-campaign-v2.preview.emergentagent.com` → `phase2-referral.preview.
// emergentagent.com`). Old founder emails point at the dead URL. If a user
// somehow loads the React bundle from a stale preview host (Cloudflare cached
// the chunk on the old host), bounce them to the canonical host with the
// path/search/hash preserved, so things like `/founder/:token` and
// `/approve-link/:token` still work.
//
// We only redirect when:
//   • we know the canonical host (REACT_APP_BACKEND_URL is set)
//   • we're on a preview.emergentagent.com host
//   • the current host literally differs from the canonical host
// We deliberately do NOT redirect when the user is on a custom domain
// (e.g. isaiahsworld.com), that's the production domain.
try {
  if (typeof window !== "undefined" && API) {
    const canonical = new URL(API);
    const here = window.location;
    const isPreview = /\.preview\.emergentagent\.com$/i.test(here.hostname);
    const isLocalhost = here.hostname === "localhost" || here.hostname === "127.0.0.1";
    if (
      isPreview &&
      !isLocalhost &&
      here.hostname !== canonical.hostname
    ) {
      const target = `${canonical.origin}${here.pathname}${here.search}${here.hash}`;
      // eslint-disable-next-line no-console
      console.info(`[fork-redirect] migrating ${here.host} → ${canonical.host}`);
      window.location.replace(target);
    }
  }
} catch { /* never let the redirect break the boot */ }
// ---------------------------------------------------------------------------

// --- KEEP MOBILE CLIENT FLAG STICKY -----------------------------------------
// The mobile app is a React Native WebView wrapper that renders this exact web
// app; when data-client="mobile" is on <html> the app uses its brighter mobile
// background (see App.css). The WebView stamps that attribute at load, but it
// was getting lost during boot (the <html> element is reconciled) and after
// in-app navigation via the header menu, flipping the background back to the
// dark web default and keeping it dark.
//
// Robust fix: detect the native WebView directly. react-native-webview always
// injects `window.ReactNativeWebView` (present ONLY inside the mobile app, never
// in a normal browser). Whenever we're in the WebView — or have ever seen the
// mobile marker — we pin data-client="mobile" and re-apply it via a
// MutationObserver (instant) plus a short interval, so it survives the boot
// reconcile and every navigation. On the plain web browser none of these are
// true, so this is a complete no-op there.
try {
  if (typeof window !== "undefined" && document.documentElement) {
    const de = document.documentElement;
    // Read the one-time URL marker the mobile wrapper adds (?client=mobile) and
    // remember it durably; it's gone after the first SPA navigation.
    try {
      if (new URLSearchParams(window.location.search).get("client") === "mobile") {
        localStorage.setItem("iw_client", "mobile");
      }
    } catch { /* URL/storage unavailable */ }
    const isMobileClient = () => {
      try {
        return !!window.ReactNativeWebView
          || de.getAttribute("data-client") === "mobile"
          || localStorage.getItem("iw_client") === "mobile";
      } catch { return !!window.ReactNativeWebView; }
    };
    const keepMobileClient = () => {
      if (!isMobileClient()) return;
      try { localStorage.setItem("iw_client", "mobile"); } catch { /* private mode */ }
      if (de.getAttribute("data-client") !== "mobile") {
        de.setAttribute("data-client", "mobile");
      }
    };
    keepMobileClient();
    try {
      new MutationObserver(keepMobileClient).observe(de, {
        attributes: true,
        attributeFilter: ["data-client"],
      });
    } catch { /* MutationObserver unsupported */ }
    setInterval(keepMobileClient, 400);
  }
} catch { /* never let this break the boot */ }
// ---------------------------------------------------------------------------


// --- GLOBAL AXIOS REQUEST INTERCEPTOR ---------------------------------------
// Two concerns, one place:
//   1. Auto-attach the auth token (read from the same storage App.js uses).
//      Callers no longer need to pass `headers: { Authorization: ... }` by hand.
//      If a caller has already set the header, we don't override.
//   2. Set a sane default timeout (25s) for calls that didn't opt into one.
//      Stops the app from hanging forever on a flaky network.
// Opt-outs:
//   - config.skipAuth === true       → we don't attach the token
//   - config.headers.Authorization   → we don't override a manually-set header
// ---------------------------------------------------------------------------
const TOKEN_KEY = "isaiah_token";
const readAuthToken = () => {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
  } catch { return null; }
};

axios.interceptors.request.use(
  (config) => {
    try {
      // Default timeout, only set if the caller hasn't already picked one.
      if (!config.timeout) config.timeout = 25000;

      // Only attach tokens to our own API (same-origin), not to asset CDNs.
      const url = (config.url || "");
      const sameOrigin = API && (url.startsWith(API) || url.startsWith("/api"));
      if (!sameOrigin || config.skipAuth) return config;

      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        const token = readAuthToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      }
    } catch { /* never let the interceptor itself throw */ }
    return config;
  },
  (err) => Promise.reject(err)
);

// --- GLOBAL AXIOS RESPONSE INTERCEPTOR --------------------------------------
// FastAPI returns validation failures as
//   { detail: [{ type, loc, msg, input, ctx }, ...] }
// Any component that renders `err.response.data.detail` directly without a
// helper will crash React with "Objects are not valid as a React child".
// We rewrite the field to a plain string on every error response so the
// whole app is immune to this regression, new forms don't have to remember
// to import `formatApiDetail` themselves. The original array/object is kept
// under `detail_raw` in case any caller needs field-level inspection.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const data = error?.response?.data;
      if (data && data.detail !== undefined && data.detail !== null && typeof data.detail !== "string") {
        data.detail_raw = data.detail;
        data.detail = formatApiDetail(data.detail);
      }
    } catch { /* never let the interceptor itself throw */ }
    return Promise.reject(error);
  }
);
// ---------------------------------------------------------------------------

// --- Suppress benign "ResizeObserver loop limit exceeded" -----------------
// This is a self-recovering browser warning fired when a ResizeObserver
// callback triggers a layout change. Chrome/Safari handle it correctly on
// the next frame, but the React dev overlay and our /api/errors/capture
// reporter both surface it as a blocking error. We capture it during the
// CAPTURE phase (before the React overlay's bubble-phase listener) and
// stop propagation so the rest of the app never sees it.
if (typeof window !== "undefined") {
  const isResizeObserverNoise = (msg) =>
    typeof msg === "string" && (
      msg.includes("ResizeObserver loop limit exceeded") ||
      msg.includes("ResizeObserver loop completed with undelivered notifications")
    );
  window.addEventListener("error", (e) => {
    if (isResizeObserverNoise(e?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);  // useCapture=true so we run BEFORE the React dev overlay listener
  window.addEventListener("unhandledrejection", (e) => {
    const m = e?.reason?.message || String(e?.reason || "");
    if (isResizeObserverNoise(m)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }, true);
}

// --- In-house error capture, reports uncaught errors + promise rejections ---
if (typeof window !== "undefined" && API) {
  const isResizeObserverNoise = (msg) =>
    typeof msg === "string" && msg.includes("ResizeObserver loop");
  const report = (payload) => {
    if (isResizeObserverNoise(payload.message)) return;  // never report the benign noise
    try {
      fetch(`${API}/api/errors/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: window.location.href,
          user_agent: navigator.userAgent,
          level: "error",
          source: "frontend",
          ...payload,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* no-op */ }
  };
  window.addEventListener("error", (e) => {
    if (e?.error) {
      report({ message: String(e.message || e.error).slice(0, 500), stack: (e.error && e.error.stack) || "" });
    }
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    report({ message: String((r && r.message) || r || "UnhandledPromiseRejection").slice(0, 500), stack: (r && r.stack) || "" });
  });
}

// Sentry, optional, only initializes when REACT_APP_SENTRY_DSN is set.
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  // Lazy-load to keep bundle cost 0 when Sentry is disabled
  import("@sentry/react").then((Sentry) => {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: process.env.REACT_APP_SENTRY_ENV || "production",
      tracesSampleRate: 0.1,
      sendDefaultPii: false,           // never ship kid/parent PII
      ignoreErrors: ["ResizeObserver loop limit exceeded"],
    });
    console.info("[sentry] frontend error monitoring enabled");
  });
}

// --- GA4 (optional), loads gtag only when REACT_APP_GA_ID is set ---
const GA_ID = process.env.REACT_APP_GA_ID;
if (GA_ID && typeof window !== "undefined") {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);

// ─── Stale-deploy white-screen auto-recovery ────────────────────────
// After a new deploy, a browser holding an old index.html / service-worker
// cache can request JS chunks that no longer exist on the server. The dynamic
// import then fails ("Loading chunk failed" / ChunkLoadError) BEFORE React can
// boot, producing a blank white screen that even a hard refresh won't clear
// (the stale service worker keeps serving the old shell). This handler detects
// that specific failure, wipes the SW + caches, and reloads ONCE (guarded to
// prevent reload loops) so the visitor lands on the fresh build automatically.
(function installChunkErrorRecovery() {
  const RELOAD_GUARD_KEY = "__iw_chunk_reload_at";

  function isChunkLoadError(err) {
    const msg = (err && (err.message || (typeof err === "string" ? err : err.toString()))) || "";
    return /Loading chunk [^ ]+ failed|ChunkLoadError|Loading CSS chunk [^ ]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(msg);
  }

  async function recover(err) {
    if (!isChunkLoadError(err)) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
      if (Date.now() - last < 20000) return; // already tried very recently, avoid a loop
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    } catch { /* sessionStorage blocked, still attempt one reload */ }

    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch { /* best-effort cache clear */ }

    window.location.reload();
  }

  window.addEventListener("error", (e) => recover(e && e.error));
  window.addEventListener("unhandledrejection", (e) => recover(e && e.reason));
})();


// Register service worker for PWA + offline caching
// "New version available" banner. By default it AUTO-REFRESHES after a short
// countdown so families always land on the newest build without tapping, but
// we never interrupt a child mid-activity (game/lesson pages), where we keep a
// manual "Refresh" button instead.
function showUpdateBanner() {
  if (document.getElementById('iw-update-banner')) return;
  // Show the update prompt at most ONCE per session so it can never nag
  // repeatedly (parents reported it re-appearing on every screen).
  try {
    if (sessionStorage.getItem('iw_update_prompted')) return;
    sessionStorage.setItem('iw_update_prompted', '1');
  } catch { /* sessionStorage blocked */ }
  const path = (window.location && window.location.pathname) || '';
  // Never show the update banner on auth/onboarding forms, it was overlaying
  // the Terms checkbox + Create Account CTA and auto-refreshing mid-signup.
  // The fresh SW activates on the next navigation anyway.
  if (/^\/(signup|login|complete-profile|portal\/(register|onboarding))/.test(path)) return;
  const inActivity = /^\/(learn|arcade)\//.test(path) || path === '/create' || path.startsWith('/art-studio');

  const bar = document.createElement('div');
  bar.id = 'iw-update-banner';
  bar.setAttribute('data-testid', 'app-update-banner');
  bar.style.cssText = 'position:fixed;left:50%;transform:translateX(-50%);bottom:16px;z-index:99999;display:flex;align-items:center;gap:12px;background:#064e3b;color:#fff;padding:10px 14px;border-radius:12px;box-shadow:0 8px 30px rgba(0,0,0,.35);font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;font-size:14px;border:1px solid rgba(52,211,153,.4);max-width:92vw;';
  const txt = document.createElement('span');
  const btn = document.createElement('button');
  btn.setAttribute('data-testid', 'app-update-refresh');
  btn.style.cssText = 'background:#34d399;color:#06281f;font-weight:700;border:none;border-radius:8px;padding:6px 14px;cursor:pointer;font-size:14px;';
  btn.onclick = () => { btn.textContent = 'Refreshing\u2026'; window.location.reload(); };
  bar.append(txt);

  let timer = null;
  if (inActivity) {
    // Mid-activity: never auto-reload. Manual refresh + dismiss.
    txt.textContent = '\u2728 A new version is available.';
    btn.textContent = 'Refresh';
    const dismiss = document.createElement('button');
    dismiss.textContent = '\u2715';
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.style.cssText = 'background:transparent;color:rgba(255,255,255,.6);border:none;cursor:pointer;font-size:16px;line-height:1;';
    dismiss.onclick = () => bar.remove();
    bar.append(btn, dismiss);
  } else {
    // Elsewhere: auto-refresh after a 6s countdown; let the user go now or wait.
    let secs = 6;
    txt.textContent = '\u2728 New version \u2014 updating in ' + secs + 's\u2026';
    btn.textContent = 'Update now';
    const later = document.createElement('button');
    later.setAttribute('data-testid', 'app-update-later');
    later.textContent = 'Later';
    later.style.cssText = 'background:transparent;color:rgba(255,255,255,.6);border:none;cursor:pointer;font-size:13px;';
    later.onclick = () => { if (timer) clearInterval(timer); bar.remove(); };
    bar.append(btn, later);
    timer = setInterval(() => {
      secs -= 1;
      if (secs <= 0) { clearInterval(timer); btn.textContent = 'Refreshing\u2026'; window.location.reload(); return; }
      txt.textContent = '\u2728 New version \u2014 updating in ' + secs + 's\u2026';
    }, 1000);
  }
  document.body.appendChild(bar);
}

if ('serviceWorker' in navigator) {
  // The SW uses skipWaiting + clients.claim, so a new build activates and takes
  // control on its own. We update SEAMLESSLY: when the controller changes to a
  // new SW, refresh once (guarded so it can NEVER loop) — silently on normal
  // pages, and via a one-time manual banner only when a child is mid-activity.
  let reloadedForUpdate = false;
  const hadController = !!navigator.serviceWorker.controller;
  const isActivityPath = () => {
    const p = (window.location && window.location.pathname) || '';
    return /^\/(learn|arcade)\//.test(p) || p === '/create' || p.startsWith('/art-studio');
  };

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Ignore the very first controller (fresh install on first visit) and any
    // repeat within this page load.
    if (reloadedForUpdate || !hadController) return;
    reloadedForUpdate = true;
    if (isActivityPath()) { showUpdateBanner(); return; }
    // Hard guard against any rapid reload loop: at most once per 60s.
    try {
      const last = Number(sessionStorage.getItem('iw_sw_reloaded_at') || 0);
      if (Date.now() - last < 60000) return;
      sessionStorage.setItem('iw_sw_reloaded_at', String(Date.now()));
    } catch { /* noop */ }
    window.location.reload();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      // Check for updates every 30 minutes (best-effort).
      setInterval(() => { try { reg.update(); } catch { /* noop */ } }, 30 * 60 * 1000);
    }).catch((err) => {
      console.error('[SW] Registration failed:', err);
    });
  });
}
